import { useQuery } from '@tanstack/react-query';
import RoleService from '../../../../services/roleService';
import type { Role } from '../../../../types/role';
import type { EpcProjectTeamRole } from '../../../../services/epcProjectService';

/**
 * Light-weight view of an admin-defined EPC role for the team-member picker.
 * `tag` is what the backend EpcProjectMembership.role enum expects — admins
 * can create custom-named roles, and the tag is optional on the Role itself;
 * only roles carrying one of the 7 tier tags are assignable here so
 * memberships stay strict.
 */
export interface EpcRoleOption {
  id: string;
  name: string;
  tag: EpcProjectTeamRole;
  description?: string;
}

function toEpcRoleOption(r: Role): EpcRoleOption | null {
  const id = (r._id ?? r.id ?? '').toString();
  if (!id || !r.tag) return null;
  return {
    id,
    name: r.name,
    tag: r.tag as EpcProjectTeamRole,
    description: r.description,
  };
}

/**
 * Fetch every active EPC role (built-in or admin-created). Each row carries
 * the tier tag so the team-member dropdown can both show the human-friendly
 * Role.name AND submit the canonical tag as membership.role.
 *
 * Roles without a `tag` are filtered out — the tag is optional on the Role
 * (permissions are granted directly in the matrix), so untagged roles simply
 * can't be mapped to a membership tier here.
 */
export function useEpcRoles(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['epc', 'roles', 'active'],
    queryFn: async (): Promise<EpcRoleOption[]> => {
      // Backend list endpoint supports `department` + `status` filters. We use
      // `status=active` rather than `isActive=true` because the role.controller
      // pick-list normalises status → isActive=true/false internally.
      const res = await RoleService.getRoles({
        department: 'EPC',
        status: 'active',
        limit: 100,
      });
      const docs = res.data ?? [];
      return docs
        .map(toEpcRoleOption)
        .filter((r): r is EpcRoleOption => r !== null)
        // Stable order: by tier first (top of hierarchy first), then by name
        // so admins see PMO above Project Head above Manager etc.
        .sort((a, b) => {
          const order: EpcProjectTeamRole[] = [
            'epc_head',
            'pmo',
            'project_head',
            'project_coordinator',
            'department_head',
            'manager',
            'site_engineer',
          ];
          const da = order.indexOf(a.tag);
          const db = order.indexOf(b.tag);
          if (da !== db) return da - db;
          return a.name.localeCompare(b.name);
        });
    },
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: options.enabled !== false,
  });
}

/**
 * Group EPC roles by their tier tag. Lets callers say "give me every role
 * that acts as a manager" without scanning the array each render.
 */
export function indexEpcRolesByTag(
  roles: EpcRoleOption[] | undefined,
): Record<EpcProjectTeamRole, EpcRoleOption[]> {
  const out: Record<EpcProjectTeamRole, EpcRoleOption[]> = {
    epc_head: [],
    pmo: [],
    project_head: [],
    project_coordinator: [],
    department_head: [],
    manager: [],
    site_engineer: [],
  };
  if (!roles) return out;
  for (const r of roles) {
    out[r.tag].push(r);
  }
  return out;
}
